import { RefObject, useEffect, useRef } from 'react'

type UseAutoScrollOptions = {
    /** Whether auto-scroll is enabled. Defaults to true. */
    enabled?: boolean
}

/**
 * Automatically scrolls to the end of a container when the dependency array changes.
 *
 * Returns a ref to place at the end of your scrollable content. When the watched
 * array changes (e.g., new messages), it scrolls smoothly to bring the ref into view.
 *
 * Uses `block: 'nearest'` to prevent the entire page from scrolling when the
 * component is embedded in a larger layout.
 *
 * @param items - Array to watch for changes (e.g., messages)
 * @param options - Optional configuration
 * @returns A ref to attach to an element at the end of your scrollable content
 *
 * @example
 * ```tsx
 * const MyChat: FC = () => {
 *   const messagesEndRef = useAutoScroll(messages)
 *
 *   return (
 *     <div style={{ overflow: 'auto' }}>
 *       {messages.map((msg) => <Message key={msg.id} {...msg} />)}
 *       <div ref={messagesEndRef} />
 *     </div>
 *   )
 * }
 * ```
 */
export const useAutoScroll = <T>(
    items: readonly T[],
    options?: UseAutoScrollOptions,
): RefObject<HTMLDivElement | null> => {
    const ref = useRef<HTMLDivElement>(null)
    const enabled = options?.enabled ?? true

    useEffect(() => {
        if (enabled) {
            ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [items, enabled])

    return ref
}
